/**
 * Telegram Service
 *
 * Service for Telegram Bot API interactions.
 * Provides methods for bot configuration, sending messages,
 * handling updates, and webhook management.
 */

// =============================================================================
// Types and Interfaces
// =============================================================================

/** Bot token configuration */
export interface TelegramConfig {
  botToken: string;
  webhookUrl?: string;
}

/** Bot info response from getMe */
export interface TelegramBot {
  id: number;
  isBot: boolean;
  firstName: string;
  username: string;
  canJoinGroups: boolean;
  canReadAllGroupMessages: boolean;
  supportsInlineQueries: boolean;
}

/** Telegram user */
export interface TelegramUser {
  id: number;
  isBot: boolean;
  firstName: string;
  lastName?: string;
  username?: string;
  languageCode?: string;
}

/** Telegram chat */
export interface TelegramChat {
  id: number;
  type: 'private' | 'group' | 'supergroup' | 'channel';
  title?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
}

/** Telegram message */
export interface TelegramMessage {
  messageId: number;
  from?: TelegramUser;
  chat: TelegramChat;
  date: number;
  text?: string;
  caption?: string;
  replyToMessage?: TelegramMessage;
  photo?: TelegramPhotoSize[];
  document?: TelegramDocument;
}

/** Photo size info */
export interface TelegramPhotoSize {
  fileId: string;
  fileUniqueId: string;
  width: number;
  height: number;
  fileSize?: number;
}

/** Document info */
export interface TelegramDocument {
  fileId: string;
  fileUniqueId: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
}

/** Update from Telegram */
export interface TelegramUpdate {
  updateId: number;
  message?: TelegramMessage;
  editedMessage?: TelegramMessage;
  channelPost?: TelegramMessage;
  editedChannelPost?: TelegramMessage;
  callbackQuery?: TelegramCallbackQuery;
}

/** Callback query from inline buttons */
export interface TelegramCallbackQuery {
  id: string;
  from: TelegramUser;
  message?: TelegramMessage;
  inlineMessageId?: string;
  chatInstance: string;
  data?: string;
}

/** Inline keyboard button */
export interface InlineKeyboardButton {
  text: string;
  url?: string;
  callbackData?: string;
}

/** Inline keyboard markup */
export interface InlineKeyboard {
  inlineKeyboard: InlineKeyboardButton[][];
}

/** Reply keyboard button */
export interface ReplyKeyboardButton {
  text: string;
  requestContact?: boolean;
  requestLocation?: boolean;
}

/** Reply keyboard markup */
export interface ReplyKeyboard {
  keyboard: ReplyKeyboardButton[][];
  resizeKeyboard?: boolean;
  oneTimeKeyboard?: boolean;
  selective?: boolean;
}

/** Send message options */
export interface SendMessageOptions {
  parseMode?: 'HTML' | 'Markdown' | 'MarkdownV2';
  disableWebPagePreview?: boolean;
  disableNotification?: boolean;
  replyToMessageId?: number;
  replyMarkup?: InlineKeyboard | ReplyKeyboard;
}

/** Webhook info */
export interface WebhookInfo {
  url: string;
  hasCustomCertificate: boolean;
  pendingUpdateCount: number;
  ipAddress?: string;
  lastErrorDate?: number;
  lastErrorMessage?: string;
  lastSynchronizationErrorDate?: number;
  maxConnections?: number;
  allowedUpdates?: string[];
}

/** Bot command */
export interface BotCommand {
  command: string;
  description: string;
}

/** API response wrapper */
interface TelegramApiResponse<T> {
  ok: boolean;
  result?: T;
  description?: string;
  errorCode?: number;
}

// =============================================================================
// Service State
// =============================================================================

/** Internal service state */
interface TelegramServiceState {
  botToken: string | null;
  webhookUrl: string | null;
  botInfo: TelegramBot | null;
  isPolling: boolean;
  pollingInterval: number;
  pollingTimeoutId: ReturnType<typeof setTimeout> | null;
  lastUpdateId: number;
  updateHandlers: Array<(update: TelegramUpdate) => Promise<void>>;
}

const state: TelegramServiceState = {
  botToken: null,
  webhookUrl: null,
  botInfo: null,
  isPolling: false,
  pollingInterval: 5000,
  pollingTimeoutId: null,
  lastUpdateId: 0,
  updateHandlers: [],
};

// =============================================================================
// API Helper
// =============================================================================

const TELEGRAM_API_BASE = 'https://api.telegram.org';

/** Make API request to Telegram */
async function telegramRequest<T>(
  method: string,
  params?: Record<string, unknown>
): Promise<T> {
  if (!state.botToken) {
    throw new Error('Telegram bot token not configured');
  }

  const url = `${TELEGRAM_API_BASE}/bot${state.botToken}/${method}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: params ? JSON.stringify(params) : undefined,
  });

  const data: TelegramApiResponse<T> = await response.json();

  if (!data.ok) {
    throw new Error(data.description || `Telegram API error: ${method}`);
  }

  return data.result as T;
}

/** Convert snake_case response to camelCase */
function toCamelCase<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(toCamelCase) as T;
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelKey] = toCamelCase(value);
    }
    return result as T;
  }

  return obj as T;
}

/** Convert camelCase params to snake_case */
function toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    if (value !== undefined && value !== null) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        result[snakeKey] = toSnakeCase(value as Record<string, unknown>);
      } else {
        result[snakeKey] = value;
      }
    }
  }
  return result;
}

// =============================================================================
// Configuration
// =============================================================================

/**
 * Configure the Telegram service with bot token
 */
export function configureTelegram(config: TelegramConfig): void {
  state.botToken = config.botToken;
  state.webhookUrl = config.webhookUrl || null;
  state.botInfo = null;
}

/**
 * Check if Telegram service is configured
 */
export function isTelegramConfigured(): boolean {
  return state.botToken !== null && state.botToken.length > 0;
}

/**
 * Get current bot token (masked for security)
 */
export function getTelegramToken(): string | null {
  if (!state.botToken) return null;
  const parts = state.botToken.split(':');
  if (parts.length !== 2) return '***';
  return `${parts[0]}:${'*'.repeat(Math.min(parts[1].length, 20))}`;
}

/**
 * Get full bot token (use with caution)
 */
export function getTelegramTokenFull(): string | null {
  return state.botToken;
}

/**
 * Clear Telegram configuration
 */
export function clearTelegramConfig(): void {
  stopPolling();
  state.botToken = null;
  state.webhookUrl = null;
  state.botInfo = null;
  state.lastUpdateId = 0;
}

// =============================================================================
// Bot Info
// =============================================================================

/**
 * Get bot information (validates token)
 */
export async function getTelegramBot(): Promise<TelegramBot> {
  const response = await telegramRequest<Record<string, unknown>>('getMe');
  const bot = toCamelCase<TelegramBot>(response);
  state.botInfo = bot;
  return bot;
}

/**
 * Get cached bot info (or fetch if not cached)
 */
export async function getTelegramBotCached(): Promise<TelegramBot | null> {
  if (state.botInfo) {
    return state.botInfo;
  }
  try {
    return await getTelegramBot();
  } catch {
    return null;
  }
}

// =============================================================================
// Messages
// =============================================================================

/**
 * Send a text message
 */
export async function sendTelegramMessage(
  chatId: string | number,
  text: string,
  options?: SendMessageOptions
): Promise<TelegramMessage> {
  const params: Record<string, unknown> = {
    chatId,
    text,
  };

  if (options) {
    const snakeOptions = toSnakeCase(options as unknown as Record<string, unknown>);
    Object.assign(params, snakeOptions);

    // Handle reply markup separately
    if (options.replyMarkup) {
      params.reply_markup = JSON.stringify(toSnakeCase(options.replyMarkup as unknown as Record<string, unknown>));
    }
  }

  const response = await telegramRequest<Record<string, unknown>>('sendMessage', params);
  return toCamelCase<TelegramMessage>(response);
}

/**
 * Send a photo
 */
export async function sendTelegramPhoto(
  chatId: string | number,
  photo: string,
  caption?: string,
  options?: Omit<SendMessageOptions, 'disableWebPagePreview'>
): Promise<TelegramMessage> {
  const params: Record<string, unknown> = {
    chatId,
    photo,
  };

  if (caption) {
    params.caption = caption;
  }

  if (options) {
    const snakeOptions = toSnakeCase(options as unknown as Record<string, unknown>);
    Object.assign(params, snakeOptions);

    if (options.replyMarkup) {
      params.reply_markup = JSON.stringify(toSnakeCase(options.replyMarkup as unknown as Record<string, unknown>));
    }
  }

  const response = await telegramRequest<Record<string, unknown>>('sendPhoto', params);
  return toCamelCase<TelegramMessage>(response);
}

/**
 * Send a document
 */
export async function sendTelegramDocument(
  chatId: string | number,
  document: string,
  caption?: string,
  options?: Omit<SendMessageOptions, 'disableWebPagePreview'>
): Promise<TelegramMessage> {
  const params: Record<string, unknown> = {
    chatId,
    document,
  };

  if (caption) {
    params.caption = caption;
  }

  if (options) {
    const snakeOptions = toSnakeCase(options as unknown as Record<string, unknown>);
    Object.assign(params, snakeOptions);

    if (options.replyMarkup) {
      params.reply_markup = JSON.stringify(toSnakeCase(options.replyMarkup as unknown as Record<string, unknown>));
    }
  }

  const response = await telegramRequest<Record<string, unknown>>('sendDocument', params);
  return toCamelCase<TelegramMessage>(response);
}

// =============================================================================
// Updates and Polling
// =============================================================================

/**
 * Get updates (for polling mode)
 */
export async function getTelegramUpdates(
  offset?: number,
  limit?: number,
  timeout?: number
): Promise<TelegramUpdate[]> {
  const params: Record<string, unknown> = {};

  if (offset !== undefined) params.offset = offset;
  if (limit !== undefined) params.limit = limit;
  if (timeout !== undefined) params.timeout = timeout;

  const response = await telegramRequest<Array<Record<string, unknown>>>('getUpdates', params);
  return response.map((update) => toCamelCase<TelegramUpdate>(update));
}

/**
 * Register an update handler
 */
export function onTelegramUpdate(handler: (update: TelegramUpdate) => Promise<void>): () => void {
  state.updateHandlers.push(handler);
  return () => {
    const index = state.updateHandlers.indexOf(handler);
    if (index > -1) {
      state.updateHandlers.splice(index, 1);
    }
  };
}

/**
 * Handle an incoming update
 */
export async function handleTelegramUpdate(update: TelegramUpdate): Promise<void> {
  for (const handler of state.updateHandlers) {
    try {
      await handler(update);
    } catch (error) {
      console.error('Telegram update handler error:', error);
    }
  }
}

/**
 * Start polling for updates
 */
export function startPolling(intervalMs: number = 5000): void {
  if (state.isPolling) {
    console.warn('Telegram polling already active');
    return;
  }

  state.isPolling = true;
  state.pollingInterval = intervalMs;

  const poll = async () => {
    if (!state.isPolling) return;

    try {
      const updates = await getTelegramUpdates(
        state.lastUpdateId > 0 ? state.lastUpdateId + 1 : undefined,
        100,
        30
      );

      for (const update of updates) {
        state.lastUpdateId = update.updateId;
        await handleTelegramUpdate(update);
      }
    } catch (error) {
      console.error('Telegram polling error:', error);
    }

    if (state.isPolling) {
      state.pollingTimeoutId = setTimeout(poll, state.pollingInterval);
    }
  };

  poll();
}

/**
 * Stop polling for updates
 */
export function stopPolling(): void {
  state.isPolling = false;
  if (state.pollingTimeoutId) {
    clearTimeout(state.pollingTimeoutId);
    state.pollingTimeoutId = null;
  }
}

/**
 * Check if polling is active
 */
export function isPollingActive(): boolean {
  return state.isPolling;
}

/**
 * Set polling interval
 */
export function setPollingInterval(intervalMs: number): void {
  state.pollingInterval = intervalMs;
}

/**
 * Get polling interval
 */
export function getPollingInterval(): number {
  return state.pollingInterval;
}

// =============================================================================
// Webhook
// =============================================================================

/**
 * Set webhook URL
 */
export async function setTelegramWebhook(
  url: string,
  options?: {
    certificate?: string;
    ipAddress?: string;
    maxConnections?: number;
    allowedUpdates?: string[];
    dropPendingUpdates?: boolean;
    secretToken?: string;
  }
): Promise<boolean> {
  const params: Record<string, unknown> = { url };

  if (options) {
    const snakeOptions = toSnakeCase(options as unknown as Record<string, unknown>);
    Object.assign(params, snakeOptions);
  }

  const response = await telegramRequest<boolean>('setWebhook', params);

  if (response) {
    state.webhookUrl = url;
  }

  return response;
}

/**
 * Delete webhook
 */
export async function deleteTelegramWebhook(dropPendingUpdates?: boolean): Promise<boolean> {
  const params: Record<string, unknown> = {};
  if (dropPendingUpdates !== undefined) {
    params.drop_pending_updates = dropPendingUpdates;
  }

  const response = await telegramRequest<boolean>('deleteWebhook', params);

  if (response) {
    state.webhookUrl = null;
  }

  return response;
}

/**
 * Get webhook info
 */
export async function getTelegramWebhookInfo(): Promise<WebhookInfo> {
  const response = await telegramRequest<Record<string, unknown>>('getWebhookInfo');
  return toCamelCase<WebhookInfo>(response);
}

// =============================================================================
// Bot Commands
// =============================================================================

/**
 * Set bot commands
 */
export async function setTelegramCommands(commands: BotCommand[]): Promise<boolean> {
  const snakeCommands = commands.map((cmd) => ({
    command: cmd.command,
    description: cmd.description,
  }));

  return await telegramRequest<boolean>('setMyCommands', {
    commands: snakeCommands,
  });
}

/**
 * Get bot commands
 */
export async function getTelegramCommands(): Promise<BotCommand[]> {
  const response = await telegramRequest<Array<{ command: string; description: string }>>('getMyCommands');
  return response;
}

/**
 * Delete all bot commands
 */
export async function deleteTelegramCommands(): Promise<boolean> {
  return await telegramRequest<boolean>('deleteMyCommands');
}

// =============================================================================
// Utilities
// =============================================================================

/**
 * Answer callback query (for inline button responses)
 */
export async function answerCallbackQuery(
  callbackQueryId: string,
  options?: {
    text?: string;
    showAlert?: boolean;
    url?: string;
    cacheTime?: number;
  }
): Promise<boolean> {
  const params: Record<string, unknown> = {
    callback_query_id: callbackQueryId,
  };

  if (options) {
    const snakeOptions = toSnakeCase(options as unknown as Record<string, unknown>);
    Object.assign(params, snakeOptions);
  }

  return await telegramRequest<boolean>('answerCallbackQuery', params);
}

/**
 * Get file download URL
 */
export async function getTelegramFileUrl(fileId: string): Promise<string> {
  if (!state.botToken) {
    throw new Error('Telegram bot token not configured');
  }

  const response = await telegramRequest<{ file_path: string }>('getFile', {
    file_id: fileId,
  });

  return `${TELEGRAM_API_BASE}/file/bot${state.botToken}/${response.file_path}`;
}

/**
 * Validate a bot token format
 */
export function validateTelegramToken(token: string): boolean {
  // Telegram bot tokens have format: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz
  const tokenRegex = /^\d+:[A-Za-z0-9_-]{35}$/;
  return tokenRegex.test(token);
}

/**
 * Extract chat ID from various sources
 */
export function extractChatId(source: TelegramUpdate | TelegramMessage): number | null {
  if ('updateId' in source) {
    // It's an update
    const update = source as TelegramUpdate;
    if (update.message) return update.message.chat.id;
    if (update.editedMessage) return update.editedMessage.chat.id;
    if (update.channelPost) return update.channelPost.chat.id;
    if (update.editedChannelPost) return update.editedChannelPost.chat.id;
    if (update.callbackQuery?.message) return update.callbackQuery.message.chat.id;
    return null;
  } else {
    // It's a message
    return (source as TelegramMessage).chat.id;
  }
}

// =============================================================================
// Export Service Interface
// =============================================================================

export const telegramService = {
  // Configuration
  configure: configureTelegram,
  isConfigured: isTelegramConfigured,
  getToken: getTelegramToken,
  getTokenFull: getTelegramTokenFull,
  clearConfig: clearTelegramConfig,

  // Bot info
  getMe: getTelegramBot,
  getMeCached: getTelegramBotCached,

  // Messages
  sendMessage: sendTelegramMessage,
  sendPhoto: sendTelegramPhoto,
  sendDocument: sendTelegramDocument,

  // Updates
  getUpdates: getTelegramUpdates,
  handleUpdate: handleTelegramUpdate,
  onUpdate: onTelegramUpdate,

  // Polling
  startPolling,
  stopPolling,
  isPollingActive,
  setPollingInterval,
  getPollingInterval,

  // Webhook
  setWebhook: setTelegramWebhook,
  deleteWebhook: deleteTelegramWebhook,
  getWebhookInfo: getTelegramWebhookInfo,

  // Commands
  setCommands: setTelegramCommands,
  getCommands: getTelegramCommands,
  deleteCommands: deleteTelegramCommands,

  // Utilities
  answerCallbackQuery,
  getFileUrl: getTelegramFileUrl,
  validateToken: validateTelegramToken,
  extractChatId,
};

export default telegramService;
